import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { X } from "@phosphor-icons/react";
import {
  ReviewLayout,
  ReviewSidebar,
  ReviewSidebarHeader,
  ReviewSidebarTitle,
  ReviewSidebarBody,
  ReviewSidebarFooter,
  DocumentViewerPane,
  ViewerToolbar,
  ViewerBody,
  FieldGroup,
  FieldLabel,
  TextInput,
  EvidenceSnippet,
  SearchWrapper,
  SearchResults,
  SearchResultItem,
  SearchResultEmpty,
  ResultName,
  ResultMeta,
  SelectedCard,
  ClearBtn,
  IdentityPanel,
  IdentityRow,
  AutoMatchBanner,
  AutoMatchLabel,
  UseMatchButton,
  SectionHeading,
  PrimaryBtn,
  SecondaryBtn,
  DangerBtn,
  BtnRow,
  LinkBtn,
  ChipRow,
  Chip,
  DegradedBanner,
  LoadingState,
  MutedText,
  MetaText,
  SidebarTabRow,
  SidebarTab,
  TabDot,
} from "../FeeNoticeQueueStyled";
import ConfidencePill from "../../../components/ConfidencePill";
import { normalizeConfidence } from "../../MailIntake/utils";
import ReadOnlyDocumentViewer from "../../../components/Document/ReadOnlyDocumentViewer";
import {
  resolveFeeNoticeDocumentSource,
  STATUS_LABELS,
  formatIsoDate,
  describeDetection,
} from "../utils";
import {
  checkWithheldLine,
  computeFeePrefills,
  VA_AGENCY_CASE_TYPE,
} from "../feeMath";
import CostInvoiceStrip from "./CostInvoiceStrip";
import DuplicateFlagsBanner from "./DuplicateFlagsBanner";
import MatterResolutions from "./MatterResolutions";
import FeeSection from "./FeeSection";
import InvoiceSection from "./InvoiceSection";
import {
  fetchMatterIdentity,
  searchMatters as searchMailMatters,
  clearMatterSearch as clearMailMatterSearch,
} from "../../../slice/MailIntakeSlice";
import {
  releaseFeeNotice,
  confirmFeeNoticeMatter,
  fetchFeeNoticeContext,
  fetchFeeNoticeInvoice,
  confirmFeeNotice,
  dismissFeeNotice,
  removeFeeNotice,
  fetchFeeNoticeQueue,
  setFeeNoticeView,
} from "../../../slice/FeeNoticeSlice";
import { buildDuplicateDismissReason } from "../utils";

const EXTRACTED_FIELD_LABELS = {
  resolution_date: "Resolution Date",
  total_retroactive_award: "Total Retroactive Award",
  attorney_fee_awarded: "Attorney Fee Awarded",
  amount_withheld_for_fees: "Amount Withheld for Fees (letter's own line)",
  decision_date: "Underlying Decision Date",
};

const ExtractedField = ({ fieldKey, field }) => {
  if (!field) return null;
  const label = EXTRACTED_FIELD_LABELS[fieldKey] || fieldKey;
  const displayValue =
    field.value === true
      ? "Yes"
      : field.value === false
      ? "No"
      : field.value ?? "—";
  return (
    <FieldGroup>
      <FieldLabel style={{ display: "flex", justifyContent: "space-between" }}>
        <span>{label}</span>
        <ConfidencePill value={normalizeConfidence(field.confidence)} />
      </FieldLabel>
      <MetaText style={{ fontSize: 14, color: "inherit" }}>{displayValue}</MetaText>
      {field.source && <EvidenceSnippet>&ldquo;{field.source}&rdquo;</EvidenceSnippet>}
    </FieldGroup>
  );
};

const ReviewView = () => {
  const dispatch = useDispatch();
  const {
    currentItem,
    matterUpdating,
    context,
    contextLoading,
    confirming,
    dismissing,
    removing,
    canRemoveUnmatched,
    reviewSession,
    invoice,
  } = useSelector((state) => state.feeNotices);
  // Fee-notice matter search reuses the mail sorter's search thunk/state
  // directly (`mailIntake.matterSearchResults`) rather than duplicating
  // it in the fee-notices slice, since AssignView's search already lives
  // there and the two features never search concurrently in one tab.
  const {
    matterIdentity,
    matterIdentityLoading,
    matterSearchResults,
    matterSearchLoading,
  } = useSelector((state) => state.mailIntake);

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showChangeMatter, setShowChangeMatter] = useState(false);
  const searchTimerRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const [amount, setAmount] = useState("");
  const [feePercent, setFeePercent] = useState("");
  const [resolutionDate, setResolutionDate] = useState("");
  const [adminFeeOverride, setAdminFeeOverride] = useState(null);
  const [referralOverride, setReferralOverride] = useState(null);
  const [referralPartnerId, setReferralPartnerId] = useState("");

  const [showDismissBox, setShowDismissBox] = useState(false);
  const [dismissReason, setDismissReason] = useState("");

  // "review" (the letter's data entry, unchanged) | "invoice" (the
  // client cost-reimbursement invoice — Matthew, 2026-07-27: view/
  // edit/send it without leaving the letter). The document viewer
  // pane stays on the SOC/FAN either way.
  const [sidebarTab, setSidebarTab] = useState("review");

  // Document viewing reuses the shared read-only Apryse viewer
  // (ReadOnlyDocumentViewer / useDocumentLoader) that every other DMS
  // surface already uses, instead of a bespoke WebViewer bootstrap —
  // that gives this pane the same retry/timeout/LOAD_ERROR handling
  // (and a visible "Preview unavailable" state instead of an endless
  // blank) for free. `resolveFeeNoticeDocumentSource` plugs in as a
  // `resolveSource` override because an unsorted letter's PDF resolves
  // through the mail-intake item detail endpoint, not a Document id.
  const resolveViewerSource = useCallback(
    () => resolveFeeNoticeDocumentSource(currentItem),
    [currentItem]
  );

  // Once a matter is set, pull the sidebar context (SF resolutions,
  // prefills, duplicate flags) and the client identity card. Re-runs
  // whenever the matter is re-pointed (confirmFeeNoticeMatter.fulfilled
  // nulls out `context`, and matter_id itself changes).
  useEffect(() => {
    if (currentItem?.matter_id) {
      dispatch(fetchMatterIdentity(currentItem.matter_id));
      dispatch(fetchFeeNoticeContext(currentItem.id));
      // Cost-invoice state has to load HERE, not in the Cost Invoice
      // tab. The tab is what used to fetch it, and the tab is only
      // mounted once someone clicks it — so on arrival this screen knew
      // nothing about money owed, and the gate banner below plus the
      // disabled Confirm both read undefined and stood down. The
      // reviewer's first news that an invoice was owed was the server
      // refusing their Confirm: the wall Matthew objected to, with the
      // warning that was supposed to prevent it sitting right there
      // unable to fire (2026-08-28).
      //
      // Safe to call on open: the invoice GET assembles a preview and
      // saves nothing. Nothing is created and no payment link is minted
      // until the reviewer presses Create invoice, which POSTs.
      dispatch(fetchFeeNoticeInvoice(currentItem.id));
    }
  }, [dispatch, currentItem?.matter_id, currentItem?.id]);

  // Seed the fee form once context resolves (or once per item when no
  // matter/context exists yet). Deliberately keyed on the context
  // object identity so further reviewer keystrokes are never clobbered
  // by this effect re-running.
  useEffect(() => {
    const fields = currentItem?.extracted_data?.fields || {};
    const feeAwarded = fields.attorney_fee_awarded?.value;

    if (context?.prefills) {
      setAmount(context.prefills.amount);
      setFeePercent(context.prefills.fee_percent);
      setResolutionDate(context.prefills.resolution_date || "");
    } else {
      setAmount(fields.total_retroactive_award?.value ?? "");
      setFeePercent(feeAwarded === false ? "0" : "");
      setResolutionDate(fields.resolution_date?.value || "");
    }
    setAdminFeeOverride(null);
    setReferralOverride(null);
    setReferralPartnerId("");
  }, [context, currentItem?.id]);

  // Reset transient UI state whenever a letter is opened — including
  // re-claiming the same letter after Back to queue. The pane stays
  // mounted (engine warmth), so currentItem.id alone would leave
  // dismiss/search/tab state from the prior visit.
  useEffect(() => {
    setSearchQuery("");
    setShowSearchResults(false);
    setShowChangeMatter(false);
    setShowDismissBox(false);
    setDismissReason("");
    setSidebarTab("review");
    dispatch(clearMailMatterSearch());
  }, [currentItem?.id, reviewSession, dispatch]);

  useEffect(() => {
    const handler = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const matterContext = context?.matter_context;
  const isVaAgency = matterContext?.case_type === VA_AGENCY_CASE_TYPE;

  const feeFields = useMemo(
    () =>
      computeFeePrefills({
        amount,
        feePercent,
        resolutionDate,
        caseType: matterContext?.case_type || "",
        gordonPartners: !!matterContext?.gordon_partners,
        sourceType: matterContext?.source_type || "",
        hasSource: !!matterContext?.source_id,
        referralFeePercent: referralOverride,
        adminFee: adminFeeOverride,
      }),
    [
      amount,
      feePercent,
      resolutionDate,
      matterContext,
      referralOverride,
      adminFeeOverride,
    ]
  );

  // The letter's own "amount withheld for fees" line vs the fee
  // section's arithmetic — recomputes live as the reviewer edits
  // (see checkWithheldLine in feeMath for the Paul Green story).
  const withheldCheck = useMemo(
    () =>
      checkWithheldLine({
        statedWithheld:
          currentItem?.extracted_data?.fields?.amount_withheld_for_fees
            ?.value,
        amount,
        feePercent,
        grossAttorneyFee: feeFields.gross_attorney_fee,
      }),
    [currentItem, amount, feePercent, feeFields]
  );

  const handleFeeFieldChange = useCallback((key, value) => {
    switch (key) {
      case "amount":
        setAmount(value);
        break;
      case "fee_percent":
        setFeePercent(value);
        break;
      case "resolution_date":
        setResolutionDate(value);
        break;
      case "admin_fee":
        setAdminFeeOverride(value);
        break;
      case "referral_fee_percent":
        setReferralOverride(value);
        break;
      case "referral_partner_id":
        setReferralPartnerId(value);
        break;
      default:
        break;
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim().length >= 2) {
      searchTimerRef.current = setTimeout(() => {
        dispatch(searchMailMatters(value.trim()));
      }, 300);
    } else {
      dispatch(clearMailMatterSearch());
    }
  };

  const handleSelectMatter = useCallback(
    async (matter) => {
      setSearchQuery("");
      setShowSearchResults(false);
      setShowChangeMatter(false);
      dispatch(clearMailMatterSearch());
      const result = await dispatch(
        confirmFeeNoticeMatter({ id: currentItem.id, matterId: matter.id })
      );
      if (!confirmFeeNoticeMatter.fulfilled.match(result)) {
        message.error(result.payload || "Failed to confirm the matter");
      }
    },
    [dispatch, currentItem?.id]
  );

  const handleAutoMatchClick = useCallback(async () => {
    if (!currentItem?.auto_match?.matter_id) return;
    const result = await dispatch(
      confirmFeeNoticeMatter({
        id: currentItem.id,
        matterId: currentItem.auto_match.matter_id,
      })
    );
    if (!confirmFeeNoticeMatter.fulfilled.match(result)) {
      message.error(result.payload || "Failed to confirm the matter");
    }
  }, [dispatch, currentItem]);


  // ONE Confirm button (Matthew, 2026-07-26): the button itself speaks
  // the letter's outcome. When the letter awarded no attorney fee and
  // the reviewer entered no dollars, confirming records the fact of
  // the FAN/SOC in DMS only (no Salesforce write — there is nothing
  // to write). Typing an award amount flips it back to a normal
  // dollars confirm, so the label always matches what will happen.
  const noFeeMode =
    currentItem?.extracted_data?.fields?.attorney_fee_awarded?.value ===
      false && !(parseFloat(amount) > 0);

  // Confirming is terminal — it writes the fee to Salesforce and the
  // letter leaves the queue — so the backend refuses while the client
  // still owes the firm its advanced costs (Matthew, 2026-08-27). Say
  // so here rather than letting the reviewer finish and be refused.
  // Only known once the Cost Invoice tab has loaded; the refusal itself
  // covers the reviewer who never opens it.
  const confirmGate = invoice?.confirm_gate;
  const invoiceBlocksConfirm = Boolean(confirmGate?.blocked) && !noFeeMode;
  // A dot on the tab for the one state that costs the firm money if it
  // is missed: costs are owed and nothing has gone out. Catches the eye
  // on a short laptop screen where the strip may be the only thing
  // scrolled past.
  const invoiceNeedsAttention =
    Boolean(confirmGate?.blocked) && !(invoice?.sends?.length > 0);

  const handleConfirm = useCallback(async () => {
    if (!currentItem || confirming || invoiceBlocksConfirm) return;
    let payload;
    if (noFeeMode) {
      payload = {};
      if (feeFields.resolution_date) {
        payload.resolution_date = feeFields.resolution_date;
      }
    } else {
      payload = { ...feeFields };
      if (referralPartnerId.trim()) {
        payload.referral_partner_id = referralPartnerId.trim();
      }
    }
    const result = await dispatch(
      confirmFeeNotice({
        id: currentItem.id,
        fields: payload,
        noFeeAwarded: noFeeMode,
      })
    );
    if (confirmFeeNotice.fulfilled.match(result)) {
      message.success(
        noFeeMode
          ? "Recorded — no fee awarded. Nothing sent to Salesforce."
          : "Confirmed — saved to the queue"
      );
      dispatch(fetchFeeNoticeQueue());
    } else {
      const failure = result.payload || {};
      message.error(failure.message || "Failed to confirm this letter");
      if (failure.code === "invoice_not_sent") {
        // Refused because the client still owes the firm its advanced
        // costs. An error toast alone would leave the reviewer hunting
        // for what to do; open the tab that clears it.
        setSidebarTab("invoice");
      }
    }
  }, [dispatch, currentItem, confirming, feeFields, referralPartnerId,
      noFeeMode, invoiceBlocksConfirm]);

  const handleDismiss = useCallback(async () => {
    if (!currentItem) return;
    const result = await dispatch(
      dismissFeeNotice({ id: currentItem.id, reason: dismissReason })
    );
    if (dismissFeeNotice.fulfilled.match(result)) {
      setShowDismissBox(false);
      setDismissReason("");
      dispatch(fetchFeeNoticeQueue());
    } else {
      message.error(result.payload || "Failed to dismiss this letter");
    }
  }, [dispatch, currentItem, dismissReason]);

  const handleDismissAsDuplicate = useCallback(
    async (flag) => {
      if (!currentItem) return;
      const reason = buildDuplicateDismissReason(flag);
      const result = await dispatch(
        dismissFeeNotice({ id: currentItem.id, reason })
      );
      if (dismissFeeNotice.fulfilled.match(result)) {
        message.success("Dismissed as duplicate");
        dispatch(fetchFeeNoticeQueue());
      } else {
        message.error(result.payload || "Failed to dismiss this letter");
      }
    },
    [dispatch, currentItem]
  );

  const handleRemove = useCallback(() => {
    if (!currentItem || removing) return;
    const suggested = currentItem.match_display_name;
    Modal.confirm({
      title: "Take this letter off the queue without a client?",
      content: suggested
        ? `This letter has a suggested client (${suggested}). Remove anyway — it will not be recorded on a client's file.`
        : "Use this only when VA did not identify the claimant. The letter stays a fee notice; it is not dismissed.",
      okText: "Remove from queue",
      onOk: async () => {
        const result = await dispatch(removeFeeNotice(currentItem.id));
        if (removeFeeNotice.fulfilled.match(result)) {
          message.success(
            "Removed from the queue — recorded as no fee, not dismissed."
          );
          dispatch(fetchFeeNoticeQueue());
        } else {
          message.error(
            result.payload || "Failed to remove this letter from the queue"
          );
        }
      },
    });
  }, [dispatch, currentItem, removing]);

  // No explicit "Release Claim" button (Matthew, 2026-07-25): leaving a
  // letter releases the soft claim implicitly, so nothing stays locked
  // to someone who walked away. Fire-and-forget — the backend's
  // stale-claim takeover is the backstop if this request is lost.
  const handleBackToQueue = useCallback(() => {
    const active =
      currentItem &&
      currentItem.status !== "confirmed" &&
      currentItem.status !== "dismissed";
    if (active) {
      dispatch(releaseFeeNotice(currentItem.id));
    }
    // The item is NOT cleared: this pane stays mounted (hidden) so the
    // document engine survives for the next letter — see the slice's
    // confirmFeeNotice.fulfilled note.
    dispatch(setFeeNoticeView("list"));
    dispatch(fetchFeeNoticeQueue());
  }, [dispatch, currentItem]);

  if (!currentItem) {
    return (
      <ReviewLayout>
        <LoadingState>Loading…</LoadingState>
      </ReviewLayout>
    );
  }

  const extractedFields = currentItem.extracted_data?.fields || {};
  const fileName = currentItem.document_name || "Fee notice letter";
  const busy = matterUpdating || confirming || dismissing || removing;
  const showRemove =
    canRemoveUnmatched
    && !currentItem.matter_id
    && noFeeMode;
  const detection = describeDetection(currentItem.classification);

  // The editable inputs display raw reviewer keystrokes (or the seeded
  // prefill) — NOT the recomputed `feeFields`. Controlling them with the
  // normalized math output made every keystroke snap back to "0"/"x.00",
  // which read as "I can't edit anything" (Matthew, staging review
  // 2026-07-24). Derived fields still render from the live recompute,
  // and the confirm payload still submits the normalized `feeFields`.
  const displayFeeFields = {
    ...feeFields,
    resolution_date: resolutionDate,
    amount,
    fee_percent: feePercent,
    admin_fee: adminFeeOverride ?? feeFields.admin_fee,
    referral_fee_percent: referralOverride ?? feeFields.referral_fee_percent,
    referral_partner_id: referralPartnerId,
  };

  // Answer "why is this Fee % what it is?" inline instead of leaving a
  // silent 0/blank the reviewer has to second-guess.
  const contingencyRate = matterContext?.contingency_fee_rate;
  const feeAwardedValue = extractedFields.attorney_fee_awarded?.value;
  let feeHint = null;
  if (feeAwardedValue === false) {
    feeHint = "The letter indicates no attorney fee was awarded — Fee % defaults to 0.";
  } else if (contingencyRate != null) {
    feeHint = context?.prefills
      ? `Fee % prefilled from the matter's contingency fee rate (${contingencyRate}%).`
      : `Matter's contingency fee rate: ${contingencyRate}%.`;
  } else if (context && matterContext) {
    feeHint =
      "This matter has no contingency fee rate in Salesforce — enter the Fee % from the letter.";
  }

  return (
    <ReviewLayout>
      <ReviewSidebar>
        <ReviewSidebarHeader>
          <div>
            <ReviewSidebarTitle>Review Fee Notice</ReviewSidebarTitle>
            <MetaText>{STATUS_LABELS[currentItem.status] || currentItem.status}</MetaText>
          </div>
          <LinkBtn type="button" onClick={handleBackToQueue}>
            Back to queue
          </LinkBtn>
        </ReviewSidebarHeader>

        <SidebarTabRow>
          <SidebarTab
            type="button"
            $active={sidebarTab === "review"}
            onClick={() => setSidebarTab("review")}
          >
            Review
          </SidebarTab>
          <SidebarTab
            type="button"
            $active={sidebarTab === "invoice"}
            onClick={() => setSidebarTab("invoice")}
            disabled={!currentItem.matter_id}
            title={
              !currentItem.matter_id
                ? "Confirm the matter match first"
                : undefined
            }
          >
            Cost Invoice
            {invoiceNeedsAttention && <TabDot aria-label="cost invoice owed" />}
          </SidebarTab>
        </SidebarTabRow>

        <CostInvoiceStrip onOpenInvoiceTab={() => setSidebarTab("invoice")} />

        <ReviewSidebarBody>
          {sidebarTab === "invoice" ? (
            <InvoiceSection disabled={busy} />
          ) : (
            <>
          {currentItem.extraction_error && (
            <DegradedBanner>
              Extraction had trouble reading this letter: {currentItem.extraction_error}.
              Fill the fields in manually below.
            </DegradedBanner>
          )}

          {/* The two detection signals (title phrase + AI reader)
              disagreed on this letter — say so up front, before the
              reviewer starts entering data (Matthew, 2026-07-25). */}
          {detection?.warning && (
            <DegradedBanner>
              <strong>Check detection:</strong> {detection.warning}
              {detection.reason && (
                <EvidenceSnippet>&ldquo;{detection.reason}&rdquo;</EvidenceSnippet>
              )}
            </DegradedBanner>
          )}

          {/* Matter matching leads the sidebar (mail-sorter posture,
              Matthew 2026-07-24): confirm WHO the letter belongs to
              first, then the financial sections below unlock. */}
          <FieldGroup>
            <SectionHeading>Matter Match</SectionHeading>
            {/* Names the suggested client instead of echoing a raw
                Salesforce id, and accepting it is now its own button:
                the banner used to commit the match on any click, so a
                reviewer looking to see who the suggestion was for had
                already accepted it. */}
            {currentItem.auto_match?.matter_id &&
              !currentItem.matter_id && (
                <AutoMatchBanner>
                  <AutoMatchLabel>
                    {currentItem.auto_match.source === "sorter"
                      ? "Matched by mail sorting"
                      : "Suggested match"}
                  </AutoMatchLabel>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ResultName>
                      {currentItem.auto_match.matter_name ||
                        currentItem.auto_match.matter_id}
                    </ResultName>
                    {currentItem.auto_match.confidence != null && (
                      <ConfidencePill
                        value={normalizeConfidence(
                          currentItem.auto_match.confidence
                        )}
                      />
                    )}
                  </div>
                  <UseMatchButton
                    type="button"
                    onClick={handleAutoMatchClick}
                    disabled={busy}
                  >
                    Use this match
                  </UseMatchButton>
                </AutoMatchBanner>
              )}

            {currentItem.matter_id && !showChangeMatter ? (
              <SelectedCard>
                <div>
                  <ResultName>
                    {currentItem.matter_display_name || currentItem.matter_id}
                  </ResultName>
                  {matterIdentityLoading && <ResultMeta>Loading client record…</ResultMeta>}
                </div>
                <ClearBtn
                  aria-label="Change matter"
                  onClick={() => setShowChangeMatter(true)}
                  disabled={busy}
                >
                  <X size={14} weight="bold" />
                </ClearBtn>
              </SelectedCard>
            ) : (
              <SearchWrapper ref={searchWrapperRef}>
                <TextInput
                  placeholder="Name, SSN last 4, VA file #, case #..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (matterSearchResults.length > 0) setShowSearchResults(true);
                  }}
                  disabled={busy}
                />
                {showSearchResults &&
                  (matterSearchResults.length > 0 || matterSearchLoading) && (
                    <SearchResults>
                      {matterSearchLoading ? (
                        <SearchResultEmpty>Searching...</SearchResultEmpty>
                      ) : (
                        matterSearchResults.map((matter) => (
                          <SearchResultItem
                            key={matter.id}
                            onClick={() => handleSelectMatter(matter)}
                          >
                            <ResultName>
                              {matter.display_name || matter.client_name}
                            </ResultName>
                            <ResultMeta>
                              {matter.case_type || ""}
                              {matter.status ? ` · ${matter.status}` : ""}
                            </ResultMeta>
                          </SearchResultItem>
                        ))
                      )}
                    </SearchResults>
                  )}
              </SearchWrapper>
            )}
          </FieldGroup>

          {/* Full stored-identifier card (same fields as the mail
              sorter's client card) so the reviewer can compare the
              letter against the firm's record before trusting the
              match. */}
          {currentItem.matter_id &&
            (matterIdentity?.client || matterIdentityLoading) && (
              <FieldGroup>
                <FieldLabel>Client Record</FieldLabel>
                <IdentityPanel>
                  {matterIdentityLoading && !matterIdentity?.client ? (
                    <MutedText>Loading client record…</MutedText>
                  ) : (
                    <>
                      <MetaText style={{ fontWeight: 600 }}>
                        {matterIdentity.client.name}
                      </MetaText>
                      {[
                        ["Date of birth", matterIdentity.client.date_of_birth],
                        [
                          "SSN",
                          matterIdentity.client.ssn_last_four
                            ? `•••-••-${matterIdentity.client.ssn_last_four}`
                            : "",
                        ],
                        ["VA file #", matterIdentity.client.va_file_number],
                        [
                          "Phone",
                          (matterIdentity.client.phones || []).join(" · "),
                        ],
                        ["Email", matterIdentity.client.email],
                      ]
                        .filter(([, value]) => Boolean(value))
                        .map(([label, value]) => (
                          <IdentityRow key={label}>
                            <span style={{ color: "#7688A3" }}>{label}</span>
                            <span>{value}</span>
                          </IdentityRow>
                        ))}
                      {(matterIdentity.client.addresses || []).map((entry) => (
                        <IdentityRow key={`${entry.label}-${entry.address}`}>
                          <span style={{ color: "#7688A3" }}>
                            {entry.label} address
                          </span>
                          <span style={{ textAlign: "right" }}>
                            {entry.address}
                          </span>
                        </IdentityRow>
                      ))}
                    </>
                  )}
                </IdentityPanel>
              </FieldGroup>
            )}

          {context?.salesforce_error && (
            <DegradedBanner>
              Couldn&rsquo;t reach Salesforce for this matter&rsquo;s history
              ({context.salesforce_error}). You can still review and confirm —
              existing resolutions and duplicate checks just won&rsquo;t show.
            </DegradedBanner>
          )}

          {currentItem.matter_id && (
            <DuplicateFlagsBanner
              flags={context?.duplicate_flags}
              onDismissAsDuplicate={handleDismissAsDuplicate}
              disabled={busy}
            />
          )}

          {matterContext && (
            <FieldGroup>
              <SectionHeading>Matter Facts</SectionHeading>
              <ChipRow>
                <Chip $on={matterContext.has_referring_attorney}>
                  Referring Attorney{matterContext.has_referring_attorney ? "" : ": No"}
                  {matterContext.has_referring_attorney && matterContext.source_name
                    ? `: ${matterContext.source_name}`
                    : ""}
                </Chip>
                <Chip $on={matterContext.gordon_partners}>
                  Gordon &amp; Partners{matterContext.gordon_partners ? "" : ": No"}
                </Chip>
                {matterContext.case_type && <Chip $on={isVaAgency}>{matterContext.case_type}</Chip>}
              </ChipRow>
            </FieldGroup>
          )}

          {currentItem.matter_id && (
            <FieldGroup>
              <SectionHeading>Existing Resolutions</SectionHeading>
              {contextLoading ? (
                <MutedText>Loading…</MutedText>
              ) : (
                <MatterResolutions
                  resolutions={context?.resolutions}
                  duplicateFlags={context?.duplicate_flags}
                />
              )}
            </FieldGroup>
          )}

          {/* How this letter got into the queue: both detection
              signals, plus the model's one-line reason. Absent on
              items queued before the hybrid detector shipped. */}
          {detection && (
            <FieldGroup>
              <SectionHeading>Detection</SectionHeading>
              <ChipRow>
                <Chip $on={!detection.warning}>{detection.phraseLabel}</Chip>
                <Chip $on={!detection.warning}>{detection.llmLabel}</Chip>
              </ChipRow>
              {detection.reason && !detection.warning && (
                <EvidenceSnippet>&ldquo;{detection.reason}&rdquo;</EvidenceSnippet>
              )}
            </FieldGroup>
          )}

          {/* Evidence sits directly above the fee entry it justifies. */}
          <FieldGroup>
            <SectionHeading>Extracted from the letter</SectionHeading>
            <ExtractedField
              fieldKey="resolution_date"
              field={extractedFields.resolution_date}
            />
            <ExtractedField
              fieldKey="total_retroactive_award"
              field={extractedFields.total_retroactive_award}
            />
            <ExtractedField
              fieldKey="attorney_fee_awarded"
              field={extractedFields.attorney_fee_awarded}
            />
            <ExtractedField
              fieldKey="amount_withheld_for_fees"
              field={extractedFields.amount_withheld_for_fees}
            />
            <ExtractedField
              fieldKey="decision_date"
              field={extractedFields.decision_date}
            />
            {/* The substance record (Matthew, 2026-07-24): the issues
                the underlying decision awarded — what this letter was
                issued in response to. */}
            {Array.isArray(extractedFields.issues?.value) &&
              extractedFields.issues.value.length > 0 && (
                <FieldGroup>
                  <FieldLabel
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Issues Awarded</span>
                    <ConfidencePill
                      value={normalizeConfidence(
                        extractedFields.issues.confidence
                      )}
                    />
                  </FieldLabel>
                  {extractedFields.issues.value.map((issue, i) => (
                    <div key={issue.text || i}>
                      {issue.condition && (
                        <MetaText style={{ fontWeight: 600 }}>
                          {issue.condition}
                          {issue.percent != null ? ` — ${issue.percent}%` : ""}
                          {issue.effective_date
                            ? `, effective ${formatIsoDate(issue.effective_date)}`
                            : ""}
                        </MetaText>
                      )}
                      <EvidenceSnippet>
                        &ldquo;{issue.text}&rdquo;
                      </EvidenceSnippet>
                    </div>
                  ))}
                </FieldGroup>
              )}
          </FieldGroup>

          {currentItem.matter_id ? (
            <FieldGroup>
              <SectionHeading>Fee Section</SectionHeading>
              {feeHint && <MutedText>{feeHint}</MutedText>}
              {withheldCheck && (
                <DegradedBanner>
                  <strong>Check the fee:</strong> the letter itself states $
                  {withheldCheck.stated.toFixed(2)} withheld for attorney fees,
                  but this fee section computes $
                  {withheldCheck.computed.toFixed(2)}. The letter&apos;s stated
                  line is what VA will actually wire
                  {withheldCheck.stated === 0
                    ? " — a $0.00 letter usually means Fee % should be 0"
                    : ""}
                  .
                </DegradedBanner>
              )}
              <FeeSection
                fields={displayFeeFields}
                onFieldChange={handleFeeFieldChange}
                gordonPartners={!!matterContext?.gordon_partners}
                disabled={busy}
              />
            </FieldGroup>
          ) : (
            <FieldGroup>
              <SectionHeading>Fee Section</SectionHeading>
              <MutedText>
                Confirm the matter match above to unlock the fee entry.
              </MutedText>
            </FieldGroup>
          )}

          {showDismissBox && (
            <FieldGroup>
              <FieldLabel>Dismiss reason</FieldLabel>
              <TextInput
                placeholder="Why isn't this a SOC/FAN letter?"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                disabled={dismissing}
              />
              <BtnRow>
                <SecondaryBtn
                  onClick={() => {
                    setShowDismissBox(false);
                    setDismissReason("");
                  }}
                  disabled={dismissing}
                >
                  Cancel
                </SecondaryBtn>
                <DangerBtn onClick={handleDismiss} disabled={dismissing}>
                  {dismissing ? "Dismissing..." : "Confirm Dismiss"}
                </DangerBtn>
              </BtnRow>
            </FieldGroup>
          )}
            </>
          )}
        </ReviewSidebarBody>

        {sidebarTab === "review" && (
        <ReviewSidebarFooter>
          {/* The banner that used to sit here said the same thing as the
              pinned strip above, in a 380px column that already stacks
              extraction, detection, Salesforce and duplicate warnings.
              The strip replaces it; the Confirm button keeps the reason
              on its tooltip. */}
          <PrimaryBtn
            onClick={handleConfirm}
            disabled={!currentItem.matter_id || busy || invoiceBlocksConfirm}
            title={
              invoiceBlocksConfirm
                ? confirmGate.reason
                : noFeeMode
                ? "Record this letter in DMS with no dollars — nothing is sent to Salesforce. Enter an award amount to switch to a normal confirm."
                : undefined
            }
          >
            {confirming
              ? "Confirming..."
              : noFeeMode
                ? "Confirm — No Fee Awarded"
                : "Confirm"}
          </PrimaryBtn>
          {showRemove && !showDismissBox && (
            <BtnRow>
              <SecondaryBtn
                onClick={handleRemove}
                disabled={busy}
                title="Takes this letter off the queue without attaching it to a client. Does not teach the system this was not a fee notice."
              >
                {removing ? "Removing..." : "Remove from queue"}
              </SecondaryBtn>
            </BtnRow>
          )}
          {!showDismissBox && (
            <BtnRow>
              <DangerBtn onClick={() => setShowDismissBox(true)} disabled={busy}>
                Dismiss
              </DangerBtn>
            </BtnRow>
          )}
        </ReviewSidebarFooter>
        )}
      </ReviewSidebar>

      <DocumentViewerPane>
        <ViewerToolbar>
          <span style={{ fontWeight: 600 }}>{fileName}</span>
        </ViewerToolbar>
        <ViewerBody>
          <ReadOnlyDocumentViewer
            documentId={`fee-notice-${currentItem.id}`}
            filename={fileName}
            resolveSource={resolveViewerSource}
          />
        </ViewerBody>
      </DocumentViewerPane>
    </ReviewLayout>
  );
};

export default ReviewView;
